/**
 * The electronic W-9 (spec 019 D28). Pure: the checks mirror the backend
 * `utils/tax-info.ts`, so the Provider sees a problem before sending. The full
 * number is sent once and never read back; the API answers with the last 4.
 */

export const TAX_CLASSIFICATIONS = [
  { value: 'individual', label: 'Individual' },
  { value: 'sole_proprietor', label: 'Sole proprietor' },
  { value: 'llc', label: 'LLC' },
  { value: 'corporation', label: 'Corporation' },
  { value: 'partnership', label: 'Partnership' },
] as const;

export type TinType = 'ssn' | 'ein';

export interface TaxInfoForm {
  legalName: string;
  classification: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  zip: string;
  tinType: TinType;
  tin: string;
  signedName: string;
  /** "I certify" box. */
  certified: boolean;
}

export type TaxInfoErrors = Partial<Record<keyof TaxInfoForm, string>>;

export const EMPTY_TAX_INFO: TaxInfoForm = {
  legalName: '',
  classification: 'individual',
  line1: '',
  line2: '',
  city: '',
  state: '',
  zip: '',
  tinType: 'ssn',
  tin: '',
  signedName: '',
  certified: false,
};

const NEVER_ISSUED_EIN_PREFIXES = [
  '00',
  '07',
  '08',
  '09',
  '17',
  '18',
  '19',
  '28',
  '29',
  '49',
  '69',
  '70',
  '78',
  '79',
  '89',
];

/** Nine digits when the number can be a real SSN or EIN, else null. */
export function normalizeTin(type: TinType, raw: string): string | null {
  const digits = (raw ?? '').replace(/[\s-]/g, '');
  if (!/^\d{9}$/.test(digits)) return null;
  if (type === 'ssn') {
    const area = digits.slice(0, 3);
    if (area === '000' || area === '666' || area.startsWith('9')) return null;
    if (digits.slice(3, 5) === '00' || digits.slice(5) === '0000') return null;
    return digits;
  }
  return NEVER_ISSUED_EIN_PREFIXES.includes(digits.slice(0, 2)) ? null : digits;
}

const norm = (s: string) =>
  s
    .normalize('NFKD')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()
    .toLowerCase();

/** The signature names the person or business on the form. */
export function signatureMatches(legalName: string, signedName: string): boolean {
  return !!norm(signedName) && norm(signedName) === norm(legalName);
}

export function taxInfoProblems(form: TaxInfoForm): TaxInfoErrors {
  const errors: TaxInfoErrors = {};
  if (!form.legalName.trim()) errors.legalName = 'Enter the name on your tax return';
  if (!TAX_CLASSIFICATIONS.some(c => c.value === form.classification)) {
    errors.classification = 'Pick a tax classification';
  }
  if (!form.line1.trim()) errors.line1 = 'Enter the street address';
  if (!form.city.trim()) errors.city = 'Enter the city';
  if (!/^[A-Za-z]{2}$/.test(form.state.trim())) errors.state = 'Use the two-letter state code';
  if (!/^\d{5}(-\d{4})?$/.test(form.zip.trim())) errors.zip = 'Enter a US ZIP code';
  if (!normalizeTin(form.tinType, form.tin)) {
    errors.tin = form.tinType === 'ssn' ? 'This is not a valid SSN' : 'This is not a valid EIN';
  }
  if (!signatureMatches(form.legalName, form.signedName)) {
    errors.signedName = 'Type the same name as above to sign';
  }
  if (!form.certified) errors.certified = 'Check the box to certify the form';
  return errors;
}

export function toTaxInfoInput(form: TaxInfoForm) {
  return {
    legalName: form.legalName.trim(),
    classification: form.classification,
    address: {
      line1: form.line1.trim(),
      line2: form.line2.trim() || null,
      city: form.city.trim(),
      state: form.state.trim().toUpperCase(),
      zip: form.zip.trim(),
    },
    tinType: form.tinType,
    tin: normalizeTin(form.tinType, form.tin) ?? '',
    signedName: form.signedName.trim(),
  };
}

/** "SSN ending in 1234": the only part of the number ever shown again. */
export function maskedTin(tinType: string, last4: string): string {
  return `${tinType.toLowerCase() === 'ein' ? 'EIN' : 'SSN'} ending in ${last4}`;
}

export function classificationLabel(value: string): string {
  return TAX_CLASSIFICATIONS.find(c => c.value === value)?.label ?? value;
}
